/**
 * Pre-Auth Selection — preset amount helpers (PS-389)
 *
 * In the store `values` always has PRE_AUTH_SLOTS entries (null = empty box) so each
 * input keeps its position. Persisted JSON holds only the numbers, in typed order.
 */

import type { PreAuthSettings } from '../types/kiosk-settings-types';

export const PRE_AUTH_SLOTS = 4;

export type PreAuthSlotError = 'required' | 'invalid' | 'duplicate' | 'exceedsCeiling' | 'belowMinimum';

export interface PreAuthValidationResult {
  slotErrors: (PreAuthSlotError | null)[];
  hasError: boolean;
}

/** Shape persisted to the backend — empty slots dropped, typed order kept. */
export interface PersistedPreAuth {
  enabled: boolean;
  values: number[];
}

/** Longest preset the input accepts — keeps values well inside Number.MAX_SAFE_INTEGER. */
export const PRE_AUTH_MAX_DIGITS = 9;

const isPositiveInt = (v: unknown): v is number =>
  typeof v === 'number' && Number.isSafeInteger(v) && v > 0;

/** Pad / truncate persisted values to PRE_AUTH_SLOTS slots, keeping only positive whole numbers. */
export function normalizePreAuthValues(raw: unknown): (number | null)[] {
  const list = Array.isArray(raw) ? raw : [];
  return Array.from({ length: PRE_AUTH_SLOTS }, (_, i) => {
    const v = typeof list[i] === 'string' && list[i] !== '' ? Number(list[i]) : list[i];
    return isPositiveInt(v) ? v : null;
  });
}

/** Shape sent to the backend — empty slots dropped, typed order kept. */
export function sanitizePreAuth(p: PreAuthSettings | undefined): PersistedPreAuth {
  return {
    enabled: p?.enabled ?? false,
    values: (p?.values ?? []).filter(isPositiveInt),
  };
}

/**
 * Lowest preset the kiosk accepts (PS-388): ceil(Default Credit ÷ 10), inclusive.
 * `ceiling` = machine Default Credit in major units; omitted/null/0 = no minimum.
 */
export function getPreAuthMinimum(ceiling?: number | null): number | null {
  if (ceiling == null || !(ceiling > 0)) return null;
  // toFixed drops float noise (e.g. 40.000000000001 / 10) so an exact multiple of 10 isn't rounded up
  return Math.ceil(Number((ceiling / 10).toFixed(6)));
}

/**
 * Validates when enabled OR when any box is filled (values are kept while off,
 * so a half-filled set must not be saved). All-empty + off is valid.
 * `ceiling` = machine Default Credit in major units; omitted/null = no ceiling and no minimum.
 */
export function validatePreAuth(
  p: PreAuthSettings | undefined,
  ceiling?: number | null
): PreAuthValidationResult {
  const values = Array.from({ length: PRE_AUTH_SLOTS }, (_, i) => p?.values?.[i] ?? null);
  const anyFilled = values.some((v) => v !== null);
  if (!p?.enabled && !anyFilled) {
    return { slotErrors: values.map(() => null), hasError: false };
  }

  const minimum = getPreAuthMinimum(ceiling);
  const slotErrors = values.map((v, i): PreAuthSlotError | null => {
    if (v === null) return 'required';
    if (!isPositiveInt(v)) return 'invalid';
    if (values.findIndex((other) => other === v) !== i) return 'duplicate';
    if (ceiling != null && ceiling > 0 && v > ceiling) return 'exceedsCeiling';
    if (minimum != null && v < minimum) return 'belowMinimum';
    return null;
  });
  return { slotErrors, hasError: slotErrors.some((e) => e !== null) };
}

/** Currency symbol for an ISO 4217 alpha code (e.g. "ILS" → "₪"); falls back to the code. */
export function getCurrencySymbol(currencyCode: string | null | undefined): string {
  if (!currencyCode) return '';
  try {
    const parts = new Intl.NumberFormat(undefined, { style: 'currency', currency: currencyCode }).formatToParts(0);
    return parts.find((p) => p.type === 'currency')?.value ?? currencyCode;
  } catch {
    return currencyCode;
  }
}
